import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';

import { checkChildren, useContext } from '@hooks';
import { mergeClasses } from '@utils';

import ProductCard, {
  ProductCardContext
} from '@modules/ProductCard/ProductCard';

/**
 * ProductCard.Labels is a child component that displays a collection of Labels within a ProductCard component.
 * Must be used as a child component of the ProductCard.
 *
 * @component ProductCard.Labels
 * @param {Object} props - The properties for the ProductCard.Labels component.
 * @param {React.ReactNode} props.children - The children ProductCard.Labels.Label components.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the ProductCard.Labels container.
 * @param {Object} restProps - The remaining properties to be passed to the component.
 * @returns {ReactElement} The rendered ProductCard.Labels component.
 * @example
 * <ProductCard
 *   href="#"
 *   productId={266712}
 *   isExclusive={true}
 *   isLimited={false}
 *   isPreorder={false}
 * >
 *   <ProductCard.Labels>
 *     <Label>Special Offer</Label>
 *   </ProductCard.Labels>
 *   <ProductCard.Image src="https://thumblr.uniid.it/product/266712/fdf6817198ad.jpg" alt="Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6" />
 *   <ProductCard.Title>Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6</ProductCard.Title>
 * </ProductCard>
 */
export default function ProductCardLabels({
  children,
  className,
  ...restProps
}) {
  checkChildren(children, [ProductCard.Labels.Label]);

  const { isDiscounted, isExclusive } = useContext(
    ProductCardContext,
    'ProductCard.Labels must be a child of ProductCard.'
  );

  // Determines the number of ProductCard.Labels.Label children to render based on isDiscounted and isExclusive.
  const labelsToRender = useMemo(() => {
    return React.Children.toArray(children).slice(
      0,
      (() => {
        if ((!isDiscounted || isDiscounted === 0) && !isExclusive) {
          return 2;
        }
        if (
          (isDiscounted && !isExclusive) ||
          ((!isDiscounted || isDiscounted === 0) && isExclusive)
        ) {
          return 1;
        }
        return 0;
      })()
    );
  }, [isDiscounted, isExclusive, children]);

  return (
    <div
      className={mergeClasses(
        'absolute inset-x-2 top-2 flex flex-col gap-0.5',
        className
      )}
      {...restProps}
    >
      {isExclusive && (
        <ProductCard.Labels.Label className="bg-black text-white">{t`Exclusive`}</ProductCard.Labels.Label>
      )}
      {(isDiscounted || isDiscounted !== 0) && (
        <ProductCard.Labels.Label className="bg-gradient-to-r from-[#47E9FF] to-green">
          {`-${isDiscounted}%`}
        </ProductCard.Labels.Label>
      )}
      {labelsToRender}
    </div>
  );
}

ProductCardLabels.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

ProductCardLabels.defaultProps = {
  children: undefined,
  className: ''
};
